import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { ItemSubtypeDefinition } from "./itemSubtypeDefinition";
import { RootState } from "../../store";
import { enhancedApiClient, isSuccessResponse } from "../../services/enhancedApiClient";

interface ItemSubtypeDefinitionSlice {
  definitions: ItemSubtypeDefinition[];
  formData: Partial<ItemSubtypeDefinition>;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
}

const initialState: ItemSubtypeDefinitionSlice = {
  definitions: [],
  formData: {
    productType: "",
    itemType: "",
    subtypeName: "",
    optionValue: "",
    optionLabel: "",
    selectionType: "single",
    isRequired: false,
    isActive: true,
    displayOrder: 0,
    optionOrder: 0,
    description: "",
  },
  loading: false,
  error: null,
  pagination: null,
};

// Fetch all subtype definitions
export const fetchItemSubtypeDefinitions = createAsyncThunk(
  "itemSubtypeDefinition/fetchItemSubtypeDefinitions",
  async (filters?: { productType?: string; itemType?: string; subtypeName?: string; isActive?: boolean }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.productType) queryParams.append('productType', filters.productType);
      if (filters?.itemType) queryParams.append('itemType', filters.itemType);
      if (filters?.subtypeName) queryParams.append('subtypeName', filters.subtypeName);
      if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());

      const response = await enhancedApiClient.get(`/item-subtype-definitions?${queryParams.toString()}`);
      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to fetch item subtype definitions");
      }
    } catch (error: any) {
      console.error("Fetch item subtype definitions error:", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to fetch item subtype definitions";
      return rejectWithValue(errorMessage);
    }
  }
);

// Create subtype definition
export const createItemSubtypeDefinition = createAsyncThunk(
  "itemSubtypeDefinition/createItemSubtypeDefinition",
  async (data: Partial<ItemSubtypeDefinition>, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.post("/item-subtype-definitions", data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to create item subtype definition");
      }
    } catch (error: any) {
      console.error("Create item subtype definition error:", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to create item subtype definition";
      return rejectWithValue(errorMessage);
    }
  }
);

// Update subtype definition
export const updateItemSubtypeDefinition = createAsyncThunk(
  "itemSubtypeDefinition/updateItemSubtypeDefinition",
  async ({ id, data }: { id: string; data: Partial<ItemSubtypeDefinition> }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.put(`/item-subtype-definitions/${id}`, data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to update item subtype definition");
      }
    } catch (error: any) {
      console.error("Update item subtype definition error:", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to update item subtype definition";
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete subtype definition
export const deleteItemSubtypeDefinition = createAsyncThunk(
  "itemSubtypeDefinition/deleteItemSubtypeDefinition",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.delete(`/item-subtype-definitions/${id}`);

      // For delete operations, success: true is sufficient (data field may not be present)
      if (response.success === true) {
        return id;
      } else {
        return rejectWithValue("Failed to delete item subtype definition");
      }
    } catch (error: any) {
      console.error("Delete item subtype definition error:", error);
      const errorMessage =
        error.response?.data?.error?.message ||
        error.message ||
        "Failed to delete item subtype definition";
      return rejectWithValue(errorMessage);
    }
  }
);

export const itemSubtypeDefinitionSlice = createSlice({
  name: "itemSubtypeDefinition",
  initialState,
  reducers: {
    setFormData: (state, action: PayloadAction<Partial<ItemSubtypeDefinition>>) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    resetForm: (state) => {
      state.formData = {
        productType: "",
        itemType: "",
        subtypeName: "",
        optionValue: "",
        optionLabel: "",
        selectionType: "single",
        isRequired: false,
        isActive: true,
        displayOrder: 0,
        optionOrder: 0,
        description: "",
      };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchItemSubtypeDefinitions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemSubtypeDefinitions.fulfilled, (state, action) => {
        state.loading = false;
        state.definitions = action.payload || [];
      })
      .addCase(fetchItemSubtypeDefinitions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createItemSubtypeDefinition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createItemSubtypeDefinition.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) {
          state.definitions.unshift(action.payload);
        }
        state.formData = {
          productType: "",
          itemType: "",
          subtypeName: "",
          optionValue: "",
          optionLabel: "",
          selectionType: "single",
          isRequired: false,
          isActive: true,
          displayOrder: 0,
          optionOrder: 0,
          description: "",
        };
      })
      .addCase(createItemSubtypeDefinition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update
      .addCase(updateItemSubtypeDefinition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateItemSubtypeDefinition.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.definitions.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.definitions[index] = action.payload;
        }
        state.formData = {
          productType: "",
          itemType: "",
          subtypeName: "",
          optionValue: "",
          optionLabel: "",
          selectionType: "single",
          isRequired: false,
          isActive: true,
          displayOrder: 0,
          optionOrder: 0,
          description: "",
        };
      })
      .addCase(updateItemSubtypeDefinition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete
      .addCase(deleteItemSubtypeDefinition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteItemSubtypeDefinition.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.definitions = state.definitions.filter((d) => d.id !== action.payload);
      })
      .addCase(deleteItemSubtypeDefinition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFormData, resetForm } = itemSubtypeDefinitionSlice.actions;

// Selectors
export const selectItemSubtypeDefinitions = (state: RootState) => state.itemSubtypeDefinition.definitions;
export const selectItemSubtypeDefinitionLoading = (state: RootState) => state.itemSubtypeDefinition.loading;
export const selectItemSubtypeDefinitionError = (state: RootState) => state.itemSubtypeDefinition.error;
export const selectItemSubtypeDefinitionPagination = (state: RootState) => state.itemSubtypeDefinition.pagination;

export default itemSubtypeDefinitionSlice.reducer;

